import { Component, createRef } from 'preact';
import { connect } from 'unistore/preact';
import cx from 'classnames';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import { Text } from 'preact-i18n';
import style from './style.css';
import { WEBSOCKET_MESSAGE_TYPES, DEVICE_FEATURE_UNITS } from '../../../../../server/utils/constants';
import { checkAndConvertUnit } from '../../../../../server/utils/units';
import get from 'get-value';
import withIntlAsProp from '../../../utils/withIntlAsProp';
import ApexChartComponent from './ApexChartComponent';
import { getDeviceName } from '../../../utils/device';
import { formatHttpError } from '../../../utils/formatErrors';

dayjs.extend(localizedFormat);

const ONE_HOUR_IN_MINUTES = 60;
const TWELVE_HOURS_IN_MINUTES = 12 * 60;
const ONE_DAY_IN_MINUTES = 24 * 60;
const THREE_DAYS_IN_MINUTES = 3 * 24 * 60;
const SEVEN_DAYS_IN_MINUTES = 7 * 24 * 60;
const THIRTY_DAYS_IN_MINUTES = 30 * 24 * 60;
const THREE_MONTHS_IN_MINUTES = 3 * 30 * 24 * 60;
const ONE_YEAR_IN_MINUTES = 365 * 24 * 60;

const intervalByName = {
  'last-hour': ONE_HOUR_IN_MINUTES,
  'last-twelve-hours': TWELVE_HOURS_IN_MINUTES,
  'last-day': ONE_DAY_IN_MINUTES,
  'last-three-days': THREE_DAYS_IN_MINUTES,
  'last-week': SEVEN_DAYS_IN_MINUTES,
  'last-month': THIRTY_DAYS_IN_MINUTES,
  'last-three-months': THREE_MONTHS_IN_MINUTES,
  'last-year': ONE_YEAR_IN_MINUTES
};

const UNITS_WHEN_DOWN_IS_POSITIVE = [
  DEVICE_FEATURE_UNITS.WATT_HOUR,
  DEVICE_FEATURE_UNITS.KILOWATT_HOUR,
  DEVICE_FEATURE_UNITS.WATT,
  DEVICE_FEATURE_UNITS.KILOWATT,
  DEVICE_FEATURE_UNITS.AQI,
  DEVICE_FEATURE_UNITS.PPM,
  DEVICE_FEATURE_UNITS.PPB,
  DEVICE_FEATURE_UNITS.DECIBEL,
  DEVICE_FEATURE_UNITS.UV_INDEX
];

const notNullNotUndefined = value => {
  return value !== undefined && value !== null;
};

const average = arr => arr.reduce((p, c) => p + c, 0) / arr.length;

const getDeviceValueByAggregateFunction = (value, aggregateFunction) => {
  if (aggregateFunction === 'min') {
    return value.min_value;
  } else if (aggregateFunction === 'max') {
    return value.max_value;
  } else if (aggregateFunction === 'sum') {
    return value.sum_value;
  } else if (aggregateFunction === 'count') {
    return value.count_value;
  } else if (aggregateFunction === 'avg') {
    return value.value;
  }
  // The default is the "avg", as before this feature
  // we were not defining an aggregate function
  return value.value;
};

const roundWith2DecimalIfNeeded = value => {
  if (!notNullNotUndefined(value)) {
    return null;
  }
  // we want to avoid displaying "15.00" if it's just 15
  if (Number.isInteger(value)) {
    return Math.round(value);
  }
  return parseFloat(value).toFixed(2);
};

const calculateVariation = (firstValue, lastValue) => {
  if (!notNullNotUndefined(firstValue) || !notNullNotUndefined(lastValue)) {
    return null;
  }
  if (firstValue === 0 && lastValue === 0) {
    return 0;
  }
  if (firstValue === 0 && lastValue > firstValue) {
    return Infinity;
  }
  if (firstValue === 0 && lastValue < firstValue) {
    return -Infinity;
  }
  return Math.round(((lastValue - firstValue) / Math.abs(firstValue)) * 100);
};

const allEqual = arr => arr.every(val => val === arr[0]);

// Compute the values displayed in the header of the box (last value + variation over the period)
// from the per-series summaries, ignoring the series hidden by the user in the chart legend.
// The variation is the one of the averaged value, so the two numbers describe the same thing:
// "the average of the visible series is now X, that is Y% more/less than at the start of the period".
const getHeaderValues = (featuresSummary, hiddenSeriesIndexes) => {
  const visibleSummaries = featuresSummary.filter(
    (summary, index) => summary !== null && !hiddenSeriesIndexes.includes(index)
  );
  if (visibleSummaries.length === 0) {
    return { variation: undefined, lastValueRounded: null };
  }
  const firstValue = average(visibleSummaries.map(summary => summary.firstValue));
  const lastValue = average(visibleSummaries.map(summary => summary.lastValue));
  return {
    variation: calculateVariation(firstValue, lastValue),
    lastValueRounded: roundWith2DecimalIfNeeded(lastValue)
  };
};

const getPeriodLabel = (interval, offset, language) => {
  const endDate = dayjs().subtract(offset, 'minute');
  const startDate = endDate.subtract(interval, 'minute');
  if (interval <= ONE_DAY_IN_MINUTES) {
    return `${startDate.locale(language).format('D MMM, HH:mm')} → ${endDate.locale(language).format('D MMM, HH:mm')}`;
  }
  return `${startDate.locale(language).format('D MMM YYYY')} → ${endDate.locale(language).format('D MMM YYYY')}`;
};

const INTERVAL_LABELS = {
  [ONE_HOUR_IN_MINUTES]: 'dashboard.boxes.chart.lastHour',
  [TWELVE_HOURS_IN_MINUTES]: 'dashboard.boxes.chart.lastTwelveHours',
  [ONE_DAY_IN_MINUTES]: 'dashboard.boxes.chart.lastDay',
  [THREE_DAYS_IN_MINUTES]: 'dashboard.boxes.chart.lastThreeDays',
  [SEVEN_DAYS_IN_MINUTES]: 'dashboard.boxes.chart.lastSevenDays',
  [THIRTY_DAYS_IN_MINUTES]: 'dashboard.boxes.chart.lastThirtyDays',
  [THREE_MONTHS_IN_MINUTES]: 'dashboard.boxes.chart.lastThreeMonths',
  [ONE_YEAR_IN_MINUTES]: 'dashboard.boxes.chart.lastYear'
};

class Chartbox extends Component {
  dropdownRef = createRef();

  handleClickOutside = event => {
    if (this.dropdownRef.current && !this.dropdownRef.current.contains(event.target)) {
      this.setState({ dropdown: false });
    }
  };

  toggleDropdown = () => {
    this.setState({
      dropdown: !this.state.dropdown
    });
  };
  switchToLastHourView = async e => {
    e.preventDefault();
    await this.setState({
      interval: ONE_HOUR_IN_MINUTES,
      offset: 0,
      dropdown: false
    });
    this.getData();
  };
  switchToLastTwelveHourView = async e => {
    e.preventDefault();
    await this.setState({
      interval: TWELVE_HOURS_IN_MINUTES,
      offset: 0,
      dropdown: false
    });
    this.getData();
  };
  switchToOneDayView = async e => {
    e.preventDefault();
    await this.setState({
      interval: ONE_DAY_IN_MINUTES,
      offset: 0,
      dropdown: false
    });
    this.getData();
  };
  switchTo3DaysView = async e => {
    e.preventDefault();
    await this.setState({
      interval: THREE_DAYS_IN_MINUTES,
      offset: 0,
      dropdown: false
    });
    this.getData();
  };
  switchTo7DaysView = async e => {
    e.preventDefault();
    await this.setState({
      interval: SEVEN_DAYS_IN_MINUTES,
      offset: 0,
      dropdown: false
    });
    this.getData();
  };
  switchTo30DaysView = async () => {
    await this.setState({
      interval: THIRTY_DAYS_IN_MINUTES,
      offset: 0,
      dropdown: false
    });
    this.getData();
  };
  switchTo3monthsView = async () => {
    await this.setState({
      interval: THREE_MONTHS_IN_MINUTES,
      offset: 0,
      dropdown: false
    });
    this.getData();
  };
  switchToYearlyView = async () => {
    await this.setState({
      interval: ONE_YEAR_IN_MINUTES,
      offset: 0,
      dropdown: false
    });
    this.getData();
  };
  navigateToPreviousPeriod = async () => {
    await this.setState(prevState => ({
      offset: prevState.offset + prevState.interval
    }));
    this.getData();
  };
  navigateToNextPeriod = async () => {
    if (this.state.offset === 0) {
      return;
    }
    await this.setState(prevState => ({
      offset: Math.max(0, prevState.offset - prevState.interval)
    }));
    this.getData();
  };
  resetToCurrentPeriod = async () => {
    if (this.state.offset === 0) {
      return;
    }
    await this.setState({ offset: 0 });
    this.getData();
  };
  handleWebsocketConnected = ({ connected }) => {
    // When the websocket is disconnected, we refresh the data when the websocket is reconnected
    if (!connected) {
      this.wasDisconnected = true;
    } else if (this.wasDisconnected) {
      this.getData();
      this.wasDisconnected = false;
    }
  };
  getData = async () => {
    let deviceFeatures = this.props.box.device_features;
    let deviceFeatureNames = this.props.box.device_feature_names;

    if (!deviceFeatures) {
      // migrate all box (one device feature)
      if (this.props.box.device_feature) {
        deviceFeatures = [this.props.box.device_feature];
      } else {
        return;
      }
    }
    // counted AFTER the guard above: a chart the user just added to a
    // dashboard has no device_features yet, and reading .length first threw
    // on every single render of the editor canvas
    let nbFeaturesDisplayed = deviceFeatures.length;
    // if there is no device selected
    if (deviceFeatures.length === 0) {
      await this.setState({
        emptySeries: true,
        loading: false
      });
      return;
    }
    await this.setState({ loading: true, error: null, errorDetail: null });
    try {
      const maxStates = 300;

      const queryParams = {
        interval: this.state.interval,
        max_states: this.props.box.group_by ? undefined : maxStates,
        group_by: this.props.box.group_by,
        device_features: deviceFeatures.join(',')
      };
      if (this.state.offset > 0) {
        queryParams.offset = this.state.offset;
      }

      const data = await this.props.httpClient.get(`/api/v1/device_feature/aggregated_states`, queryParams);

      let emptySeries = true;

      // The unit of a device feature can change after the box was configured (device re-created,
      // integration updated...), and the "units" saved in the box config is only a snapshot taken
      // when the feature was added. So we use the unit returned by the API as the source of truth,
      // and only fallback on the box config when the feature has no unit.
      // Note: "unit" (singular) is the legacy attribute, kept for boxes saved before "units" existed.
      const unitsByFeature = data.map((oneFeature, index) => {
        const unitFromBoxConfig = this.props.box.units ? this.props.box.units[index] : this.props.box.unit;
        return get(oneFeature, 'deviceFeature.unit') || unitFromBoxConfig;
      });

      let series = [];

      if (this.props.box.chart_type === 'timeline') {
        const serie0 = {
          name: get(this.props.intl.dictionary, 'dashboard.boxes.chart.off'),
          data: []
        };
        const serie1 = {
          name: get(this.props.intl.dictionary, 'dashboard.boxes.chart.on'),
          data: []
        };
        // Segments still open at the end of the displayed period are clamped to the end of
        // that period, so browsing a past period doesn't stretch the last bar until now.
        const periodEnd = dayjs().subtract(this.state.offset, 'minute');

        const lastValueTime = Math.round(periodEnd.valueOf() / 1000) * 1000;
        data.forEach((oneFeature, index) => {
          const { values, deviceFeature, device } = oneFeature;
          const deviceFeatureName = deviceFeatureNames
            ? deviceFeatureNames[index]
            : getDeviceName(device, deviceFeature);
          if (values.length === 0) {
            nbFeaturesDisplayed = nbFeaturesDisplayed - 1;
          } else {
            values.forEach(value => {
              emptySeries = false;
              const beginTime = Math.round(new Date(value.created_at).getTime() / 1000) * 1000;
              const endTime = value.end_time
                ? Math.round(new Date(value.end_time).getTime() / 1000) * 1000
                : lastValueTime;
              const newData = {
                x: deviceFeatureName,
                y: [beginTime, endTime]
              };
              if (value.value === 0) {
                serie0.data.push(newData);
              } else {
                serie1.data.push(newData);
              }
            });
          }
        });
        series.push(serie1);
        series.push(serie0);
      } else {
        series = data.map((oneFeature, index) => {
          const oneUnit = unitsByFeature[index];
          // Convert unit display format if necessary (using 0 as a dummy value since we only need the unit)
          const userUnitPreference = this.props.user.distance_unit_preference;
          const { unit: displayUnit } = checkAndConvertUnit(0, oneUnit, userUnitPreference);

          const oneUnitTranslated = displayUnit ? this.props.intl.dictionary.deviceFeatureUnitShort[displayUnit] : null;
          const { values, deviceFeature, device } = oneFeature;
          const deviceFeatureName = deviceFeatureNames
            ? deviceFeatureNames[index]
            : getDeviceName(device, deviceFeature);
          const name = oneUnitTranslated ? `${deviceFeatureName} (${oneUnitTranslated})` : deviceFeatureName;
          return {
            name,
            data: values.map(dataPoint => {
              emptySeries = false;
              const rawValue = getDeviceValueByAggregateFunction(dataPoint, this.props.box.aggregate_function);

              // Convert the value if it is a convertible unit
              const { value: displayValue } = checkAndConvertUnit(rawValue, oneUnit, userUnitPreference);

              return [Math.round(new Date(dataPoint.created_at).getTime() / 1000) * 1000, displayValue];
            })
          };
        });
      }
      const newState = {
        series,
        loading: false,
        initialized: true,
        emptySeries,
        nbFeaturesDisplayed,
        // Cleared on every load, so turning the toggle off returns the chart
        // to an automatic axis without a reload.
        yAxisMin: undefined,
        yAxisMax: undefined
      };

      if (data.length > 0 && this.props.box.chart_type !== 'timeline') {
        const unit = unitsByFeature[0];

        // Convert distance units if necessary
        const userUnitPreference = this.props.user.distance_unit_preference;
        let displayUnit = unit;

        // We check if all deviceFeatures selected are in the same unit
        const allUnitsAreSame = this.props.box.units ? allEqual(unitsByFeature) : false;

        // First and last value of the period for each feature (one entry per series, in the same
        // order as the chart series). If all features share the same unit, they are all summarized
        // and averaged together, otherwise only the first feature is displayed.
        // The header (last value + variation) is derived from these summaries, restricted to the
        // series currently visible in the chart legend (see getHeaderValues).
        const featuresSummary = data.map((oneFeature, index) => {
          const { values } = oneFeature;
          if (values.length === 0 || (!allUnitsAreSame && index > 0)) {
            return null;
          }
          const firstElement = values[0];
          const lastElement = values[values.length - 1];
          const rawFirstValue = getDeviceValueByAggregateFunction(firstElement, this.props.box.aggregate_function);
          const rawLastValue = getDeviceValueByAggregateFunction(lastElement, this.props.box.aggregate_function);
          if (!notNullNotUndefined(rawFirstValue) || !notNullNotUndefined(rawLastValue)) {
            return null;
          }
          // Convert the value if it is a convertible unit
          const { value: firstValue, unit: firstElementUnit } = checkAndConvertUnit(
            rawFirstValue,
            unit,
            userUnitPreference
          );
          const { value: lastValue } = checkAndConvertUnit(rawLastValue, unit, userUnitPreference);
          displayUnit = firstElementUnit;
          return { firstValue, lastValue };
        });

        /*
         * A FIXED AXIS, from what the instrument can read rather than from
         * what it happened to read.
         *
         * An auto-scaled axis redraws a quiet signal as a mountain range: a
         * tank sitting near full for twelve hours fills the chart with its
         * own measurement noise and reads as a crisis. The working range is
         * already authored — it is what `min` and `max` on a feature mean —
         * so the chart can use it and put 88% at 88% of the way up.
         *
         * The widest of the features on the chart wins, so no series is
         * clipped, and the bounds go through the same unit conversion as the
         * values or a converted chart would be scaled in the stored unit.
         * A feature with no usable range simply contributes none, and if
         * none of them has one the axis stays automatic.
         */
        if (this.props.box.fixed_scale) {
          let low = null;
          let high = null;
          data.forEach(oneFeature => {
            const feature = oneFeature.deviceFeature || {};
            const min = Number(feature.min);
            const max = Number(feature.max);
            if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
              return;
            }
            const { value: convertedMin } = checkAndConvertUnit(min, unit, userUnitPreference);
            const { value: convertedMax } = checkAndConvertUnit(max, unit, userUnitPreference);
            low = low === null ? convertedMin : Math.min(low, convertedMin);
            high = high === null ? convertedMax : Math.max(high, convertedMax);
          });
          if (low !== null && high !== null && high > low) {
            newState.yAxisMin = low;
            newState.yAxisMax = high;
          }
        }

        newState.featuresSummary = featuresSummary;
        newState.variationDownIsPositive = UNITS_WHEN_DOWN_IS_POSITIVE.includes(displayUnit);
        newState.unit = displayUnit;
        Object.assign(newState, getHeaderValues(featuresSummary, this.state.hiddenSeriesIndexes));
      }
      await this.setState(newState);
    } catch (e) {
      console.error(e);
      const { errorString, errorDetailString } = formatHttpError(e);
      this.setState({
        loading: false,
        error: errorString,
        errorDetail: errorDetailString
      });
    }
  };
  updateDeviceStateWebsocket = payload => {
    if (
      this.state.offset === 0 &&
      this.state.interval === intervalByName['last-hour'] &&
      this.props.box.device_features &&
      this.props.box.device_features.includes(payload.device_feature_selector)
    ) {
      this.getData();
    }
  };
  // Called by the chart each time it is drawn, with the indexes of the series hidden through
  // the legend: recompute the header from the series that are actually visible.
  handleHiddenSeriesChange = hiddenSeriesIndexes => {
    this.setState(prevState => {
      if (
        hiddenSeriesIndexes.length === prevState.hiddenSeriesIndexes.length &&
        hiddenSeriesIndexes.every((index, position) => index === prevState.hiddenSeriesIndexes[position])
      ) {
        return null;
      }
      return {
        hiddenSeriesIndexes,
        ...getHeaderValues(prevState.featuresSummary, hiddenSeriesIndexes)
      };
    });
  };
  updateInterval = async () => {
    await this.setState({
      interval: intervalByName[this.props.box.interval],
      offset: 0
    });
  };
  constructor(props) {
    super(props);
    this.props = props;
    this.state = {
      interval: this.props.box.interval ? intervalByName[this.props.box.interval] : ONE_HOUR_IN_MINUTES,
      offset: 0,
      loading: true,
      initialized: false,
      height: 'small',
      nbFeaturesDisplayed: 0,
      featuresSummary: [],
      hiddenSeriesIndexes: []
    };
  }
  componentDidMount() {
    this.getData();
    this.props.session.dispatcher.addListener(
      WEBSOCKET_MESSAGE_TYPES.DEVICE.NEW_STATE,
      this.updateDeviceStateWebsocket
    );
    this.props.session.dispatcher.addListener('websocket.connected', this.handleWebsocketConnected);
    document.addEventListener('mousedown', this.handleClickOutside);
  }
  async componentDidUpdate(previousProps) {
    const intervalChanged = get(previousProps, 'box.interval') !== get(this.props, 'box.interval');
    const deviceFeaturesChanged = get(previousProps, 'box.device_features') !== get(this.props, 'box.device_features');
    const titleChanged = get(previousProps, 'box.title') !== get(this.props, 'box.title');
    const unitChanged = get(previousProps, 'box.unit') !== get(this.props, 'box.unit');
    if (intervalChanged) {
      await this.updateInterval(this.props.box.interval);
    }
    if (intervalChanged || deviceFeaturesChanged || titleChanged || unitChanged) {
      this.getData();
    }
  }

  componentWillUnmount() {
    this.props.session.dispatcher.removeListener(
      WEBSOCKET_MESSAGE_TYPES.DEVICE.NEW_STATE,
      this.updateDeviceStateWebsocket
    );
    this.props.session.dispatcher.removeListener('websocket.connected', this.handleWebsocketConnected);
    document.removeEventListener('mousedown', this.handleClickOutside);
  }
  render(
    props,
    {
      initialized,
      loading,
      series,
      dropdown,
      variation,
      variationDownIsPositive,
      lastValueRounded,
      interval,
      offset,
      emptySeries,
      unit,
      nbFeaturesDisplayed,
      yAxisMin,
      yAxisMax,
      error,
      errorDetail
    }
  ) {
    const { box } = this.props;
    const displayVariation = box.display_variation;
    // The hidden series are positional, in ApexCharts as in the header: recreate the chart
    // (all series visible again) when the devices of the box change, e.g. in the editor.
    // Same fallback on the legacy "device_feature" (one device) attribute as in getData.
    const deviceFeaturesKey = (box.device_features || [box.device_feature]).join(',');
    let additionalHeight = 30 * (nbFeaturesDisplayed - 1);
    if (props.box.chart_type === 'timeline') {
      additionalHeight = 55 * nbFeaturesDisplayed;
    }
    return (
      <div class={cx('card', { 'loading-border': initialized && loading, [style.cardMenuOpen]: dropdown })}>
        <div class="card-body">
          <div class={style.chartHeader}>
            <div class={cx(style.subheader)}>{box.title}</div>
            {props.box.chart_type && (
              <div class={style.chartToolbar}>
                <button
                  type="button"
                  class={style.navButton}
                  onClick={this.navigateToPreviousPeriod}
                  title={props.intl.dictionary.dashboard.boxes.chart.previousPeriod}
                  aria-label={props.intl.dictionary.dashboard.boxes.chart.previousPeriod}
                >
                  <i class="fe fe-chevron-left" />
                </button>

                <div class={style.toolbarBody} ref={this.dropdownRef}>
                  <button type="button" class={style.intervalButton} onClick={this.toggleDropdown}>
                    <Text id={INTERVAL_LABELS[interval]} />
                    <i
                      class={cx('fe fe-chevron-down', style.intervalChevron, { [style.intervalChevronOpen]: dropdown })}
                    />
                  </button>

                  {/* single line next to the period label: the live dot, or —
                      when browsing the past — an accent pill showing the range,
                      whose one tap action (the return icon says it) is coming
                      back to now */}
                  {offset > 0 ? (
                    <button
                      type="button"
                      class={style.periodRangeButton}
                      onClick={this.resetToCurrentPeriod}
                      title={props.intl.dictionary.dashboard.boxes.chart.backToNow}
                      aria-label={props.intl.dictionary.dashboard.boxes.chart.backToNow}
                    >
                      <i class="fe fe-corner-up-left" />
                      <span class={style.periodRange}>{getPeriodLabel(interval, offset, props.user.language)}</span>
                    </button>
                  ) : (
                    <div class={style.periodLive}>
                      <span class={style.liveDot} />
                      <Text id="dashboard.boxes.chart.currentPeriod" />
                    </div>
                  )}

                  <div
                    class={cx(style.dropdownMenuChart, {
                      [style.dropdownMenuOpen]: dropdown
                    })}
                  >
                    <a
                      class={cx(style.dropdownItemChart, {
                        [style.active]: interval === ONE_HOUR_IN_MINUTES
                      })}
                      onClick={this.switchToLastHourView}
                    >
                      <Text id="dashboard.boxes.chart.lastHour" />
                    </a>
                    <a
                      class={cx(style.dropdownItemChart, {
                        [style.active]: interval === TWELVE_HOURS_IN_MINUTES
                      })}
                      onClick={this.switchToLastTwelveHourView}
                    >
                      <Text id="dashboard.boxes.chart.lastTwelveHours" />
                    </a>
                    <a
                      class={cx(style.dropdownItemChart, {
                        [style.active]: interval === ONE_DAY_IN_MINUTES
                      })}
                      onClick={this.switchToOneDayView}
                    >
                      <Text id="dashboard.boxes.chart.lastDay" />
                    </a>
                    {props.box.chart_type !== 'timeline' && (
                      <a
                        className={cx(style.dropdownItemChart, {
                          [style.active]: interval === THREE_DAYS_IN_MINUTES
                        })}
                        onClick={this.switchTo3DaysView}
                      >
                        <Text id="dashboard.boxes.chart.lastThreeDays" />
                      </a>
                    )}
                    {props.box.chart_type !== 'timeline' && (
                      <a
                        className={cx(style.dropdownItemChart, {
                          [style.active]: interval === SEVEN_DAYS_IN_MINUTES
                        })}
                        onClick={this.switchTo7DaysView}
                      >
                        <Text id="dashboard.boxes.chart.lastSevenDays" />
                      </a>
                    )}
                    {props.box.chart_type !== 'timeline' && (
                      <a
                        className={cx(style.dropdownItemChart, {
                          [style.active]: interval === THIRTY_DAYS_IN_MINUTES
                        })}
                        onClick={this.switchTo30DaysView}
                      >
                        <Text id="dashboard.boxes.chart.lastThirtyDays" />
                      </a>
                    )}
                    {props.box.chart_type !== 'timeline' && (
                      <a
                        className={cx(style.dropdownItemChart, {
                          [style.active]: interval === THREE_MONTHS_IN_MINUTES
                        })}
                        onClick={this.switchTo3monthsView}
                      >
                        <Text id="dashboard.boxes.chart.lastThreeMonths" />
                      </a>
                    )}
                    {props.box.chart_type !== 'timeline' && (
                      <a
                        className={cx(style.dropdownItemChart, {
                          [style.active]: interval === ONE_YEAR_IN_MINUTES
                        })}
                        onClick={this.switchToYearlyView}
                      >
                        <Text id="dashboard.boxes.chart.lastYear" />
                      </a>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  class={style.navButton}
                  onClick={this.navigateToNextPeriod}
                  disabled={offset === 0}
                  title={props.intl.dictionary.dashboard.boxes.chart.nextPeriod}
                  aria-label={props.intl.dictionary.dashboard.boxes.chart.nextPeriod}
                >
                  <i class="fe fe-chevron-right" />
                </button>
              </div>
            )}
          </div>

          {props.box.chart_type && (
            <div>
              {displayVariation && props.box.chart_type !== 'timeline' && emptySeries === false && (
                <div class="d-flex align-items-baseline">
                  {notNullNotUndefined(lastValueRounded) && !Number.isNaN(lastValueRounded) && (
                    <div class="h1 mb-0 mr-2">
                      {lastValueRounded} {unit !== undefined && <Text id={`deviceFeatureUnitShort.${unit}`} />}
                    </div>
                  )}
                  <div
                    class={cx(style.meAuto, {
                      [style.textGreen]:
                        (variation > 0 && !variationDownIsPositive) || (variation < 0 && variationDownIsPositive),
                      [style.textYellow]: variation === 0,
                      [style.textRed]:
                        (variation > 0 && variationDownIsPositive) || (variation < 0 && !variationDownIsPositive)
                    })}
                  >
                    {variation !== undefined && (
                      <span class="d-inline-flex align-items-center lh-1">
                        {roundWith2DecimalIfNeeded(variation)}
                        <Text id="global.percent" />
                        {variation > 0 && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class={cx(style.variationIcon)}
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            stroke-width="2"
                            stroke="currentColor"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <polyline points="3 17 9 11 13 15 21 7" />
                            <polyline points="14 7 21 7 21 14" />
                          </svg>
                        )}
                        {variation === 0 && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class={cx(style.variationIcon)}
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            stroke-width="2"
                            stroke="currentColor"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                        )}
                        {variation < 0 && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class={cx(style.variationIcon)}
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            stroke-width="2"
                            stroke="currentColor"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <polyline points="3 7 9 13 13 9 21 17" />
                            <polyline points="21 10 21 17 14 17" />
                          </svg>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {emptySeries === false && props.box.display_axes && (
                <div class="mt-4">
                  <ApexChartComponent
                    key={deviceFeaturesKey}
                    series={series}
                    interval={interval}
                    user={props.user}
                    size="big"
                    chart_type={props.box.chart_type}
                    display_axes={props.box.display_axes}
                    y_axis_min={yAxisMin}
                    y_axis_max={yAxisMax}
                    colors={props.box.colors}
                    additionalHeight={additionalHeight}
                    dictionary={props.intl.dictionary}
                    onHiddenSeriesChange={this.handleHiddenSeriesChange}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div
          class={cx('dimmer', {
            active: loading && !initialized
          })}
        >
          <div class="loader" />
          <div
            class={cx('dimmer-content', {
              [style.minSizeChartLoading]: loading && !initialized
            })}
          >
            {!props.box.chart_type && (
              <div class={cx('text-center', style.bigEmptyState)}>
                <div />
                <div>
                  <i class="fe fe-alert-circle mr-2" />
                  <Text id="dashboard.boxes.chart.noChartType" />
                </div>
                <div class={style.smallTextEmptyState}>
                  <Text id="dashboard.boxes.chart.noChartTypeWarning" />
                </div>
              </div>
            )}
            {error && (
              <div class={cx('text-center', style.bigEmptyState)}>
                <div>
                  <i class="fe fe-alert-circle mr-2" />
                  {error}
                </div>
                {errorDetail && <div>{errorDetail}</div>}
              </div>
            )}
            {props.box.chart_type && (
              <div>
                {emptySeries === true && (
                  <div class={cx('text-center', style.bigEmptyState)}>
                    <div />
                    <div>
                      <i class="fe fe-alert-circle mr-2" />
                      <Text id="dashboard.boxes.chart.noValue" />
                    </div>
                  </div>
                )}
                {emptySeries === false && !props.box.display_axes && (
                  <ApexChartComponent
                    key={deviceFeaturesKey}
                    series={series}
                    interval={interval}
                    user={props.user}
                    size="big"
                    chart_type={props.box.chart_type}
                    display_axes={props.box.display_axes}
                    y_axis_min={yAxisMin}
                    y_axis_max={yAxisMax}
                    colors={props.box.colors}
                    additionalHeight={additionalHeight}
                    dictionary={props.intl.dictionary}
                    onHiddenSeriesChange={this.handleHiddenSeriesChange}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default withIntlAsProp(connect('httpClient,session,user')(Chartbox));
